(() => {
  if (customElements.get('lp-pair-addons')) return;
  class PairAddons extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.closest('form');
      this.data = JSON.parse(this.querySelector('[data-pair-data]').textContent);
      this.messages = this.data.messages;
      this.rate = Number(this.dataset.discountPercent);
      this.button = this.querySelector('[data-pair-add]');
      this.status = this.querySelector('[data-pair-status]');
      this.card = this.querySelector('.lp-pair-addons__card');
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:this.dataset.currency || 'USD'});
      this.change = () => this.sync();
      this.form?.addEventListener('change', this.change);
      this.onAddonChange = event => {
        event.stopPropagation();
        const option = event.target.selectedOptions?.[0];
        const image = event.target.closest('[data-pair-item]')?.querySelector('img');
        if (image && option?.dataset.image) {image.src = option.dataset.image; image.removeAttribute('srcset');}
        this.sync();
      };
      this.addEventListener('change', this.onAddonChange);
      this.onAddClicked = () => this.add();
      this.button.addEventListener('click', this.onAddClicked);
      this.observer = new MutationObserver(() => this.sync());
      if (this.form) this.observer.observe(this.form,{subtree:true,childList:true,attributes:true,attributeFilter:['value']});
      this.onVariant = () => queueMicrotask(() => this.sync());
      this.bind = () => {
        if (this.bus || !this.form || !window.themeCore?.EventBus) return;
        this.bus = window.themeCore.EventBus;
        this.event = 'form:' + this.form.getAttribute('id') + ':change-variant';
        this.bus.listen(this.event,this.onVariant);
      };
      this.bind();
      document.addEventListener('DOMContentLoaded',this.bind,{once:true});
      window.addEventListener('load',this.bind,{once:true});
      this.onFocus = async () => {
        if (this.busy || Date.now() - (this.refreshedAt || 0) < 15000) return;
        const before = this.fingerprint();
        try {
          await this.refresh();
          this.sync();
          if (before !== this.fingerprint()) this.status.textContent = this.messages.refreshed;
        } catch (_) { /* The add action retries and reports a failed refresh. */ }
      };
      window.addEventListener('focus',this.onFocus);
      this.sync();
    }
    disconnectedCallback() {
      this.form?.removeEventListener('change',this.change);
      this.removeEventListener('change',this.onAddonChange);
      this.button?.removeEventListener('click',this.onAddClicked);
      this.observer?.disconnect();
      this.bus?.remove(this.event,this.onVariant);
      this.bus = null;
      document.removeEventListener('DOMContentLoaded',this.bind);
      window.removeEventListener('load',this.bind);
      window.removeEventListener('focus',this.onFocus);
      this.ready = false;
    }
    money(cents) {return this.formatter.format(cents / 100);}
    discounted(price, rate = this.rate) {return Math.round(price * (100 - rate) / 100);}
    quantity() {return Number(this.form?.querySelector('[name="quantity"]')?.value || 1);}
    selection() {
      const id = Number(this.form?.querySelector('[name="id"]')?.value || this.dataset.defaultVariant);
      const main = this.data.variants.find(v => Number(v.id) === id);
      const selected = [...this.querySelectorAll('[data-pair-item]')]
        .filter(row => row.querySelector('[data-pair-check]').checked)
        .map(row => ({row,index:Number(row.dataset.index),option:row.querySelector('select').selectedOptions[0]}));
      const mask = selected.reduce((value,item) => value | (1 << item.index),0);
      const subtotal = (main?.price || 0) + selected.reduce((sum,item) => sum + Number(item.option?.dataset.price || 0),0);
      const rule = this.data.rules.find(item => item.mask === mask);
      const rate = Number(rule?.percent || this.rate);
      const total = (main?.price || 0) + selected.reduce((sum,item) => sum + this.discounted(Number(item.option?.dataset.price || 0),rate),0);
      const eligibleMain = !!main && (this.dataset.setOnly !== 'true' || main.set);
      return {main,selected,mask,subtotal,total,rule,eligibleMain};
    }
    fingerprint() {
      const {main,selected,mask} = this.selection();
      return [main?.id,main?.price,main?.available,mask,this.quantity(),...selected.flatMap(item => [item.option?.value,item.option?.dataset.price,item.option?.dataset.available])].join(':');
    }
    sync() {
      const signature = this.fingerprint() + ':' + !!this.busy;
      if (signature === this.signature) return;
      this.signature = signature;
      const {main,selected,subtotal,total,rule,eligibleMain} = this.selection();
      const unavailable = !main?.available || selected.some(item => item.option?.dataset.available !== 'true');
      const usd = this.dataset.currency === 'USD';
      this.card.hidden = !eligibleMain;
      this.button.disabled = !!this.busy || !eligibleMain || !selected.length || unavailable || this.quantity() !== 1 || !usd || !rule;
      this.button.textContent = this.busy ? this.messages.adding : this.messages.add;
      this.querySelectorAll('[data-pair-item]').forEach(row => {
        const price = Number(row.querySelector('select').selectedOptions[0]?.dataset.price || 0);
        const included = row.querySelector('[data-pair-check]').checked && usd;
        row.querySelector('[data-pair-price]').textContent = this.money(included ? this.discounted(price,Number(rule?.percent || this.rate)) : price);
        row.querySelector('[data-pair-item-original]').textContent = this.money(price);
        row.querySelector('[data-pair-item-original]').hidden = !included;
        row.querySelector('[data-pair-item-badge]').textContent = this.messages.save.replace('{{ percent }}',String(rule?.percent || this.rate));
        row.querySelector('[data-pair-item-badge]').hidden = !included;
      });
      this.querySelector('[data-pair-original]').hidden = !selected.length || !usd;
      this.querySelector('[data-pair-original]').textContent = this.money(subtotal);
      this.querySelector('[data-pair-total]').textContent = this.money(usd ? total : subtotal);
      if (!this.busy) this.status.textContent = !eligibleMain ? this.messages.single : !selected.length ? this.messages.choose : unavailable ? this.messages.unavailable : this.quantity() !== 1 ? this.messages.quantity : !usd ? this.messages.price : !rule ? this.messages.changed : '';
    }
    async refresh() {
      if (this.refreshing) return this.refreshing;
      this.refreshing = (async () => {
        const rows = [...this.querySelectorAll('[data-pair-item]')];
        const handles = [this.dataset.mainHandle,...rows.map(row => row.dataset.handle)];
        const products = await Promise.all(handles.map(async handle => {
          const response = await fetch(window.Shopify.routes.root + 'products/' + encodeURIComponent(handle) + '.js',{cache:'no-store'});
          if (!response.ok) throw new Error(this.messages.error);
          return response.json();
        }));
        if (!this.isConnected) return;
        this.data.variants = this.data.variants.map(item => {
          const fresh = products[0].variants.find(variant => Number(variant.id) === Number(item.id));
          return fresh ? {...item,price:fresh.price,available:fresh.available} : {...item,available:false};
        });
        rows.forEach((row,index) => {
          [...row.querySelector('select').options].forEach(option => {
            const fresh = products[index + 1].variants.find(variant => Number(variant.id) === Number(option.value));
            option.dataset.available = String(!!fresh?.available);
            option.disabled = !fresh?.available;
            if (fresh) option.dataset.price = String(fresh.price);
          });
        });
        this.refreshedAt = Date.now();
      })();
      try {await this.refreshing;} finally {this.refreshing = null;}
    }
    async request(path,body) {
      const response = await fetch(window.Shopify.routes.root + path,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.messages.error);
      return data;
    }
    async add() {
      this.sync();
      if (this.busy || this.button.disabled) return;
      const fingerprint = this.fingerprint();
      this.busy = true; this.sync();
      let added = false;
      let message = this.messages.added;
      try {
        await this.refresh();
        if (!this.isConnected) return;
        if (this.fingerprint() !== fingerprint) {message = this.messages.refreshed; return;}
        const {main,selected,rule,eligibleMain} = this.selection();
        if (!eligibleMain || !main.available || selected.some(item => item.option.dataset.available !== 'true') || this.quantity() !== 1 || !rule) throw new Error(this.messages.unavailable);
        const before = await fetch(window.Shopify.routes.root + 'cart.js').then(response => response.json());
        await this.request('cart/add.js',{items:[{id:Number(main.id),quantity:1},...selected.map(item => ({id:Number(item.option.value),quantity:1}))]});
        added = true;
        const mainProductId = rule.code.split('-')[1];
        const codes = (before.discount_codes || []).map(item => item.code).filter(code => {
          const parts = code.toUpperCase().split('-');
          const samePair = /^LPPAIR(?:15|30)$/.test(parts[0]) && parts[1] === mainProductId;
          return !samePair && !/^LPADD(?:V2)?-/i.test(code);
        });
        const cart = await this.request('cart/update.js',{discount:[...new Set([...codes,rule.code])].join(',')});
        const codeApplied = cart.discount_codes?.some(item => item.code.toUpperCase() === rule.code && item.applicable);
        const savingsApplied = selected.every(item => cart.items?.some(line => Number(line.id) === Number(item.option.value) && line.final_price <= this.discounted(Number(item.option.dataset.price),Number(rule.percent || this.rate))));
        if (!codeApplied && !savingsApplied) message = this.messages.discount_error;
        const api = window.themeCore?.CartApi;
        if (api) await api.makeRequest(api.actions.GET_CART);
        else window.location.assign(window.Shopify.routes.root + 'cart');
      } catch (error) {message = added ? this.messages.discount_error : (error.message || this.messages.error);}
      finally {this.busy = false; this.sync(); this.status.textContent = message;}
    }
  }
  customElements.define('lp-pair-addons',PairAddons);
})();

